import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

function buildQueryString(params = {}) {
	const query = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined || value === null || value === "") return;
		query.set(key, value);
	});
	return query.toString();
}

export const DispatcherApi = createApi({
	reducerPath: "Dispatcher",
	baseQuery: fetchBaseQuery({
		baseUrl: `${process.env.REACT_APP_BACKEND3_URL}/dispatcher`,
		credentials: "include",
		prepareHeaders: (headers) => {
			const token = localStorage.getItem("token");
			if (token) {
				headers.set("Authorization", `Bearer ${token}`);
			}
			// Backend resolves the response language from this header (see
			// backend3 middleware/localization.ts) — without it every endpoint
			// silently falls back to English regardless of the app's actual
			// language toggle, since the browser's own Accept-Language reflects
			// OS/browser locale, not localStorage("lang").
			headers.set(
				"Accept-Language",
				localStorage.getItem("lang") === "ar" ? "ar" : "en",
			);
			return headers;
		},
	}),
	tagTypes: ["ServiceRequest", "ServiceRequestHistory", "ServiceRequestTasks"],
	endpoints: (builder) => ({
		getServiceRequests: builder.query({
			query: (params) => `/service-requests?${buildQueryString(params)}`,
			providesTags: ["ServiceRequest"],
		}),
		getServiceRequestDetails: builder.query({
			query: (id) => `/service-requests/${id}`,
			providesTags: (result, error, id) => [{ type: "ServiceRequest", id }],
		}),
		getServiceRequestHistory: builder.query({
			query: (arg) => {
				const { id, page, limit } = typeof arg === "object" ? arg : { id: arg };
				const qs = buildQueryString({ page, limit });
				return `/service-requests/${id}/history${qs ? `?${qs}` : ""}`;
			},
			// Cache keyed by request id only (ignoring page) so successive pages
			// accumulate into one growing list instead of replacing each other —
			// that's what lets the history panel scroll-load instead of paginate.
			serializeQueryArgs: ({ queryArgs }) => {
				const { id } =
					typeof queryArgs === "object" ? queryArgs : { id: queryArgs };
				return id;
			},
			merge: (currentCache, newItems, { arg }) => {
				const page = typeof arg === "object" ? arg.page : undefined;
				// A refetch of page 1 (tag invalidation, remount) replaces the list
				// instead of appending, so it can't duplicate already-loaded pages.
				currentCache.data =
					!page || page <= 1
						? newItems.data || []
						: [...(currentCache.data || []), ...(newItems.data || [])];
				currentCache.pagination = newItems.pagination;
			},
			forceRefetch: ({ currentArg, previousArg }) =>
				currentArg?.page !== previousArg?.page,
			providesTags: (result, error, arg) => [
				{
					type: "ServiceRequestHistory",
					id: typeof arg === "object" ? arg.id : arg,
				},
			],
		}),
		getServiceRequestTasks: builder.query({
			query: (id) => `/service-requests/${id}/tasks`,
			providesTags: (result, error, id) => [
				{ type: "ServiceRequestTasks", id },
			],
		}),
		bulkAssignServiceRequests: builder.mutation({
			query: (payload) => ({
				url: "/service-requests/bulk-assign",
				method: "POST",
				body: payload,
			}),
			invalidatesTags: [
				"ServiceRequest",
				"ServiceRequestHistory",
				"ServiceRequestTasks",
			],
		}),
		getServiceRequestTasksOpen: builder.query({
			query: (params = {}) => {
				const qs = buildQueryString(params).replace(/%2C/g, ",");
				return `/service-requests/open-by-poi?${qs}`;
			},
			invalidatesTags: ["ServiceRequest", "ServiceRequestTasks"],
		}),
	}),
});

export const {
	useGetServiceRequestsQuery,
	useGetServiceRequestDetailsQuery,
	useGetServiceRequestHistoryQuery,
	useGetServiceRequestTasksQuery,
	useBulkAssignServiceRequestsMutation,
	useGetServiceRequestTasksOpenQuery,
} = DispatcherApi;
